import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { useQueries, useQuery } from "@tanstack/react-query";
import { conversationApiService } from "../services/conversationApiService";
import { usePhoneNumbers } from "./phoneNumberQueries";
import {
  fromConversationWithClientDtoToConversationWithClient,
  type ConversationWithClient,
} from "../types/conversationTypes";

const staleTime = 5 * 60 * 1000; // 5 minutes

// Query keys
export const conversationKeys = {
  all: ["conversation"] as const,
  list: (businessId?: string, phoneNumberId?: string, clientId?: string, userId?: string) =>
    [
      ...conversationKeys.all,
      "list",
      { businessId, phoneNumberId, clientId, userId },
    ] as const,
  detail: (id: string, businessId?: string, userId?: string) =>
    [...conversationKeys.all, "detail", { businessId, id, userId }] as const,
};

const fetchConversations = async (businessId: string, phoneNumberId: string, signal?: AbortSignal) => {
  const dtos = await conversationApiService.getList({ businessId, phoneNumberId }, signal);
  return dtos.map(fromConversationWithClientDtoToConversationWithClient)
    .filter((conversation) => conversation.businessId === businessId && !conversation.deletedAt);
};

/** Conversations across all of the business's phone numbers (the backend lists them per number). */
export const useBusinessConversations = () => {
  const { businessId } = useCurrentBusiness();
  const { user } = useAuth();
  const phoneQuery = usePhoneNumbers();
  const phoneNumberIds = phoneQuery.data?.map((phone) => phone.id) ?? [];
  const conversations = useQueries({
    queries: phoneNumberIds.map((phoneNumberId) => ({
      queryKey: conversationKeys.list(businessId, phoneNumberId, undefined, user?.id),
      queryFn: ({ signal }: { signal: AbortSignal }) => fetchConversations(businessId!, phoneNumberId, signal),
      enabled: !!user && !!businessId,
      staleTime,
    })),
  });
  const error = phoneQuery.error ?? conversations.find((query) => query.error)?.error ?? null;
  return {
    data: phoneQuery.data && conversations.every((query) => query.data)
      ? conversations.flatMap((query) => query.data ?? []) : undefined,
    error,
    isLoading: phoneQuery.isLoading || conversations.some((query) => query.isLoading),
    isFetching: phoneQuery.isFetching || conversations.some((query) => query.isFetching),
    refetch: async () => {
      const phones = await phoneQuery.refetch();
      // Phone number changes re-key the conversation queries, which then fetch on their own.
      const current = phones.data?.map((phone) => phone.id).join() === phoneNumberIds.join();
      if (current) await Promise.all(conversations.map((query) => query.refetch()));
    },
  };
};

export const useConversation = (id?: string) => {
  const { businessId } = useCurrentBusiness();
  const { user } = useAuth();
  return useQuery<ConversationWithClient, Error>({
    queryKey: conversationKeys.detail(id || "", businessId, user?.id),
    queryFn: async ({ signal }) => {
      const dto = await conversationApiService.getById(id!, signal);
      const conversation = fromConversationWithClientDtoToConversationWithClient(dto);
      if (conversation.businessId !== businessId || conversation.deletedAt) {
        throw new Error("Conversation not found in this business.");
      }
      return conversation;
    },
    enabled: !!user && !!businessId && !!id,
    staleTime,
  });
};

export const useClientConversations = (
  clientId?: string,
  phoneNumberId?: string
) => {
  const { businessId } = useCurrentBusiness();
  const { user } = useAuth();
  return useQuery<ConversationWithClient[], Error>({
    queryKey: conversationKeys.list(businessId, phoneNumberId, clientId, user?.id),
    queryFn: async ({ signal }) => {
      try {
        const dtos = await conversationApiService.getList({
          businessId: businessId || undefined,
          phoneNumberId,
          clientId,
        }, signal);
        return dtos.map(fromConversationWithClientDtoToConversationWithClient);
      } catch (error) {
        // If no conversations found, return empty array
        if (error instanceof Error && error.message.includes("not found")) {
          return [];
        }
        throw error;
      }
    },
    enabled: !!user && !!businessId && !!phoneNumberId && !!clientId,
    staleTime,
  });
};
